import { getUrls } from '@/common/composables/url/url';

/**
 * Stand-in for `AuthProviderV2` when the app runs against the offline API.
 *
 * Okta needs a network and a real MIT session, neither of which exists on a
 * plane. This mints an unsigned JWT locally instead. The API accepts it because
 * offline mode runs with SKIP_JWT_VALIDATION=true, which decodes the token
 * without verifying the signature — the same path the backend already uses for
 * local development.
 *
 * The identity is switchable at runtime (see `setIdentity`), so every persona
 * the app renders differently — super_admin, admin, support, plain key holder,
 * someone with no access at all — is reachable without touching Okta or the
 * fixture data. The chosen kerb persists in localStorage across reloads.
 *
 * This module is only ever imported through `authProvider.ts`, which picks it
 * over the real provider solely when VITE_OFFLINE_MODE is true.
 */

export interface OfflineIdentity {
  kerb: string;
  mitId: string;
  displayName: string;
  email: string;
  affiliation: string;
  department: string;
  moiraLists: string[];
  moiraStatus: string;
  role: 'super_admin' | 'admin' | 'support' | 'user';
}

const STORAGE_KEY = 'parley.offline.identity';

let identities: OfflineIdentity[] = [];
let current: OfflineIdentity | null = null;
let loaded = false;

function base64Url(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Build the token the API expects. The claim names match what
 * `validateAndExtractUser` reads: scoped_uid drives the kerb, mit_id is what
 * the role lookup uses to find the person in the (fixture) directory.
 */
function mintToken(identity: OfflineIdentity): string {
  const issuedAt = Math.floor(Date.now() / 1000);
  const [firstName, ...rest] = identity.displayName.split(' ');

  const header = { alg: 'none', typ: 'JWT' };
  const payload = {
    sub: `offline|${identity.kerb}`,
    scoped_uid: `${identity.kerb}@mit.edu`,
    email: identity.email,
    first_name: firstName ?? '',
    last_name: rest.join(' '),
    mit_id: identity.mitId,
    name: identity.displayName,
    iss: 'https://offline.invalid/oauth2/offline',
    aud: 'parley-offline',
    iat: issuedAt,
    // Long-lived on purpose: a token expiring mid-flight would drop you onto
    // the session-expired screen with no way back.
    exp: issuedAt + 60 * 60 * 24 * 30,
    scp: ['openid', 'profile', 'email', 'mit:user:all.read'],
  };

  return `${base64Url(JSON.stringify(header))}.${base64Url(JSON.stringify(payload))}.offline`;
}

/** Fetch the fixture cast from the offline API's /offline/identities route. */
async function loadIdentities(): Promise<void> {
  if (loaded) return;

  const { apiUrlBase } = getUrls();
  try {
    const response = await fetch(`${apiUrlBase}/offline/identities`);
    if (!response.ok) throw new Error(`status ${response.status}`);
    const body = (await response.json()) as { people: OfflineIdentity[] };
    identities = body.people ?? [];
  } catch (error) {
    // The API isn't up yet, or isn't in offline mode. Fall back to a single
    // synthetic super_admin so the app still boots and shows a real error from
    // the first API call rather than a blank auth screen.
    console.warn('[offline auth] could not load identities from the API:', error);
    identities = [
      {
        kerb: 'offline',
        mitId: '900000001',
        displayName: 'Offline Operator',
        email: 'offline@mit.edu',
        affiliation: 'staff',
        department: 'Information Systems & Technology',
        moiraLists: [],
        moiraStatus: '1',
        role: 'super_admin',
      },
    ];
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  current =
    identities.find((identity) => identity.kerb === stored) ??
    identities.find((identity) => identity.role === 'super_admin') ??
    identities[0] ??
    null;

  loaded = true;
}

export function listIdentities(): OfflineIdentity[] {
  return identities;
}

export function currentIdentity(): OfflineIdentity | null {
  return current;
}

/**
 * Switch identity and reload. A reload rather than a reactive swap because
 * every store was hydrated for the previous user — role, owned accounts, keys,
 * pending approvals. Re-running the bootstrap is both simpler and more honest
 * than trying to invalidate all of it.
 */
export function setIdentity(kerb: string): void {
  window.localStorage.setItem(STORAGE_KEY, kerb);
  window.location.reload();
}

export const offlineAuthProvider = {
  async configure(): Promise<void> {
    await loadIdentities();
  },

  getState(): string {
    return current ? 'authenticated' : 'unauthenticated';
  },

  async isAuthenticated(): Promise<boolean> {
    await loadIdentities();
    return current !== null;
  },

  async signIn(): Promise<void> {
    await loadIdentities();
  },

  async signOut(): Promise<void> {
    window.localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  },

  async getToken(): Promise<string | null> {
    await loadIdentities();
    return current ? mintToken(current) : null;
  },
};
